import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Tooltip, Typography } from "@mui/material";
import { useListContext } from "ra-core";
import { FC, ReactNode } from "react";
import { FunctionField, TextField } from "@/components/field";
import { Datagrid } from "@/components/list/datagrid";
import { RecPatch } from "@/dataProvider/tools";
import { ClientManageDto, ClientType } from "@/gen/api/user";
import { useUserTimeFormat } from "@/utils/userTimeFormat";


interface ClientsTableProp {
  ActionField?: ReactNode,
  selectRow?: (x:any) => void,
  includeApproveTime?: boolean,
  includeRejectTime?: boolean,
  includeRevokeTime?: boolean,
  includeSuspendTime?: boolean,
  activeStateObservationTime?: number|null,
}

interface structX { date:any, firstName:any, lastName:any, username:any }
type structY = { [k in keyof structX]: keyof ClientManageDto }

const dict: Record<string, structY> = {
  '1': {
    date: 'registerAt',
    firstName: 'r1UserFirstName',
    lastName:  'r1UserLastName',
    username:  'r1Username',
  },
  '2': {
    date: 'reviewedAt',
    firstName: 'r2UserFirstName',
    lastName:  'r2UserLastName',
    username:  'r2Username',
  },
  '3': {
    date: 'revokedAt',
    firstName: 'r3UserFirstName',
    lastName:  'r3UserLastName',
    username:  'r3Username',
  },
  '4': {
    date: 'suspendedAt',
    firstName: 'r4UserFirstName',
    lastName:  'r4UserLastName',
    username:  'r4Username',
  },
}

const ClientsTable: FC<ClientsTableProp&{[k in string]?:any}> = ({
  ActionField, selectRow, includeApproveTime, includeRejectTime, includeRevokeTime, includeSuspendTime,
  activeStateObservationTime,
  ...rest
}) => {
  const listCtx = useListContext()
  const format = useUserTimeFormat()
  
  const renderEventTimeOperator = (x:string) => {
    const bag = dict[x]
    return (d?: ClientManageDto & RecPatch) => {
      if (!d || !d[bag.date]) { return null; }
      if (x === '1') {
        if (d.clientType === ClientType.ShareSecretDataDriver || d.clientType === ClientType.ShareSecretEncryptionTool) {
          return null;
        }
      }
      
      const dateVal = (d[bag.date] || '0') as string
      const date = new Date(dateVal);
      const dateString = format(date, 'Pp')

      return (
        <>
          <Typography variant="body2">
            {dateString}
          </Typography>
          {Boolean(d[bag.username]) &&
            <Tooltip title={d[bag.username] || ''}>
              <Box>
                <Typography variant="caption">
                  By <Typography variant="caption" color="primary.main">{d[bag.firstName]} {d[bag.lastName]}</Typography>
                </Typography>                
              </Box>
            </Tooltip>}
        </>
      )
    }
  }

  return (
    <Datagrid
      hasBulkActions={false} 
      rowClick={selectRow ? (id:any, basePath:string, rec:any) => {
        selectRow(rec)
        return 'toggleSelection'
      } : undefined}
      sx={{ 
        '& th' : { py: 2 },
        '& td' : { py: 2 },
      }}
      {...rest}
      {...listCtx}
    >
      <FunctionField source="register_at" label="Registered At" 
        render={renderEventTimeOperator('1')}
      />
      {includeApproveTime &&
        <FunctionField source="reviewed_at" label="Approved At" 
          render={renderEventTimeOperator('2')}
        />}
      {includeRejectTime &&
        <FunctionField source="reviewed_at" label="Rejected At" 
          render={renderEventTimeOperator('2')}
        />}
      {includeRevokeTime &&
        <FunctionField source="revoked_at" label="Revoked At" 
          render={renderEventTimeOperator('3')}
        />}
      {includeSuspendTime &&
        <FunctionField source="suspended_at" label="Suspended At" 
          render={renderEventTimeOperator('4')}
        />}
      <TextField source="clientName" label="Client Name" sortable={false} />
      <FunctionField source="clientType" label="Client Type & Version" sortable={false} 
        render={(d?: ClientManageDto & RecPatch) => {
          if (!d) { return null; }
          const lapse = activeStateObservationTime && d.lastActiveTime ? (activeStateObservationTime - Date.parse(d.lastActiveTime)) : null
          return (
            <>
              <Typography variant="body2">
                {d.clientType === ClientType.DataDriver ? 'Database Driver'
                  : d.clientType === ClientType.EncryptionTool ? 'Encryption Tool'
                  : (d.clientType === ClientType.ShareSecretDataDriver || d.clientType === ClientType.ShareSecretEncryptionTool) ? 'Shared Secret'
                  : null
                }
              </Typography>
              <Typography variant="caption" component="p" color="success.main">
                {d.softwareVersion}
              </Typography>
              {lapse != null &&
                <Typography variant="caption" component="p" sx={{ display: 'flex', alignItems: 'center', '& svg': {fontSize: '14px', mr: 0.25} }}>
                  {lapse / 1000 / 60 > 5
                    ? <><HighlightOffIcon color="error" />Offline</>
                    : <><CheckCircleOutlineIcon color="success" />Online</>}
                </Typography>
              }
            </>
          )
        }}
      />
      <FunctionField source="osInfo" label="OS & IP" sortable={false} 
        render={(d?: ClientManageDto & RecPatch) => {
          if (!d) { return null; }
          return (
            <>
              <Typography variant="body2">
                {d.osInfo?.replace(/unix/i, "MacOS")}<br/>
                <Typography variant="caption">
                  {d.ipAddress}
                </Typography>
              </Typography>
            </>
          )
        }}
      />
      {ActionField}
    </Datagrid>
  )
}

export default ClientsTable;
